import { Link, useParams } from "@tanstack/react-router";
import { AnimatePresence, motion } from "framer-motion";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import type { Transaction } from "@/apis/gql/types";
import {
  AccountingTransactionsReadyToExportBadge,
  AccountingTransactionsTodosBadge,
} from "@/components/shared/app-navigation/sidebar/badges/company";
import AIProcessingCard from "@/components/shared/cards/ai-processing-card";
import { UploadInvoicesDrawer } from "@/components/shared/drawers/upload-invoices-drawer";
import {
  NavigationMenu,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
} from "@/components/ui/navigation-menu";
import UploadProgressBox from "@/features/upload-progress-box/UploadProgressBox";
import { useArrayQueryState } from "@/hooks/useArrayQueryState";
import { cn } from "@/lib/utils";
import { useAddInvoiceToTransaction } from "@/queries/transactions/add-invoice";
import { useApproveTransactionBooking } from "@/queries/transactions/approve-transaction-booking";
import { useDeleteInvoiceFromTransaction } from "@/queries/transactions/delete-invoice";
import { accountingTransactionsTodosFilter } from "@/queries/transactions/utils/transactions-todos-filter";

import { TransactionsTodos } from "../../shared/transactions-todos";
import { useTransactionsTodosTable } from "../../shared/use-transactions-todos-table";
import { BookingPanel, BookingPanelProvider } from "../booking-panel";
import { columns as todosColumns } from "../transaction-todos/transactions-todos-columns";
import { TableActionBar } from "./transactions-todos-action-bar";

export enum TransactionsTabsValues {
  TODOS = "transactions-todos",
}

export default function Transactions() {
  const { t } = useTranslation();
  const { company: companyId } = useParams({
    from: "/_authenticated/$company",
  });
  const [approveTransactionBooking] = useApproveTransactionBooking();
  const [addInvoiceToTransaction] = useAddInvoiceToTransaction();
  const [deleteInvoiceFromTransaction] = useDeleteInvoiceFromTransaction();

  const [isOpen, setIsOpen] = useState(false);

  const [selectedTransactionIds, setSelectedTransactionIds] =
    useArrayQueryState("selected-transaction-ids");
  const [selectedInvoiceIds, setSelectedInvoiceIds] = useState<
    Record<string, boolean>
  >({});

  const transactionTodos = useTransactionsTodosTable({
    storageKey: "accounting-transaction-todos",
    defaultFilters: accountingTransactionsTodosFilter,
    columns: todosColumns({
      onConfirmBookings: handleConfirmBankTransactionBookings,
      onUnmatch: handleUnmatch,
    }),
    rowSelection: selectedTransactionIds,
    onRowSelectionChange: setSelectedTransactionIds,
  });

  const transactionsForPanel = transactionTodos.table
    .getFilteredSelectedRowModel()
    .rows.map((row) => row.original) as Transaction[];

  const allTransactions = transactionTodos.table
    .getFilteredRowModel()
    .rows.map((row) => row.original) as Transaction[];

  const { handleNext } = useTransactionsBrowsingQueue({
    transactions: allTransactions,
  });

  return (
    <BookingPanelProvider selectedTransactionIds={selectedTransactionIds}>
      <div className="h-full flex">
        <div className="flex flex-1 h-full">
          <div className="flex-1 flex flex-col min-w-0">
            <div className="flex px-4 bg-background pb-2">
              <AIProcessingCard screen="txn-todos" className="mt-4" />
            </div>
            <div className="flex-1 flex">
              <div className="flex flex-col min-w-0 w-full relative">
                <div>
                  <NavigationMenu className="px-4 bg-background max-w-full justify-start border-b">
                    <NavigationMenuList className="space-x-0">
                      <NavigationMenuItem>
                        <Link
                          to="/$company/buchhaltung/transaktionen/todos"
                          params={{ company: companyId }}
                        >
                          <NavigationMenuLink
                            active
                            className={cn(
                              "text-gray-500 flex items-center py-3 px-6 gap-4 font-medium text-lg",
                              "inline-flex justify-center whitespace-nowrap ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
                              "border-b-[3px] text-primary border-primary cursor-default",
                            )}
                            onClick={(e) => e.preventDefault()}
                          >
                            {t("transactions.tabs.todos")}
                            <AccountingTransactionsTodosBadge />
                          </NavigationMenuLink>
                        </Link>
                      </NavigationMenuItem>
                      <NavigationMenuItem>
                        <Link
                          to="/$company/buchhaltung/transaktionen/export"
                          params={{ company: companyId }}
                        >
                          <NavigationMenuLink
                            className={cn(
                              "flex items-center py-3 px-6 gap-4 font-medium text-lg",
                              "inline-flex justify-center whitespace-nowrap ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
                              "text-gray-500",
                            )}
                          >
                            {t("transactions.tabs.export")}
                            <AccountingTransactionsReadyToExportBadge />
                          </NavigationMenuLink>
                        </Link>
                      </NavigationMenuItem>
                      <NavigationMenuItem>
                        <Link
                          to="/$company/buchhaltung/transaktionen"
                          params={{ company: companyId }}
                        >
                          <NavigationMenuLink
                            className={cn(
                              "text-gray-500 flex items-center py-3 px-6 gap-2 font-medium text-lg rounded-none",
                              "inline-flex justify-center whitespace-nowrap ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
                              "data-[active=true]:border-b-[3px] data-[active=true]:border-primary",
                            )}
                          >
                            {t("transactions.tabs.all_transactions")}
                          </NavigationMenuLink>
                        </Link>
                      </NavigationMenuItem>
                    </NavigationMenuList>
                  </NavigationMenu>
                </div>
                <div className="flex-1 flex flex-col">
                  <div className="flex-1 px-4 pb-4">
                    <TransactionsTodos {...transactionTodos}>
                      <TransactionsTodos.ActionBar>
                        <TableActionBar
                          onMatch={handleMatch}
                          selectedInvoices={selectedInvoiceIds}
                          table={transactionTodos.table}
                        />
                      </TransactionsTodos.ActionBar>
                    </TransactionsTodos>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <AnimatePresence mode="wait">
            {transactionsForPanel.length > 0 && (
              <motion.div
                key="booking-panel-wrapper"
                className="bg-background z-10 overflow-hidden"
                style={{
                  width: "50%",
                }}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{
                  duration: 0.3,
                  ease: [0.4, 0, 0.2, 1],
                }}
              >
                <BookingPanel
                  key={transactionsForPanel.map((t) => t.bankTxnId).join(",")}
                  transactions={transactionsForPanel}
                  onRowSelectionChange={setSelectedInvoiceIds}
                  rowSelection={selectedInvoiceIds}
                  onMatch={handleMatch}
                  onUnmatch={(invoiceIds) => handleUnmatch({ invoiceIds })}
                  onConfirmBookings={handleConfirmBankTransactionBookings}
                  onConfirmRawBookings={handleConfirmBankTransactionBookings}
                  view="search"
                  onClose={handleClosePanel}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
      <UploadInvoicesDrawer isOpen={isOpen} setIsOpen={setIsOpen} />
      <UploadProgressBox />
    </BookingPanelProvider>
  );

  async function handleMatch(invoiceIds?: string[]) {
    const relevantInvoiceIds = invoiceIds ?? Object.keys(selectedInvoiceIds);

    const bankTxnIds = transactionsForPanel.map((t) => t.bankTxnId);

    await addInvoiceToTransaction({
      bankTxnIds,
      invoiceIds: relevantInvoiceIds,
    });
  }

  async function handleUnmatch({
    bankTxnIds: _bankTxnIds,
    invoiceIds,
  }: { bankTxnIds?: string[]; invoiceIds?: string[] } = {}) {
    const relevantInvoiceIds = invoiceIds ?? Object.keys(selectedInvoiceIds);

    const bankTxnIds =
      _bankTxnIds ?? transactionsForPanel.map((t) => t.bankTxnId);

    await deleteInvoiceFromTransaction({
      bankTxnIds,
      invoiceIds: relevantInvoiceIds,
    });
  }

  async function handleConfirmBankTransactionBookings(
    bankTxnIds?: string[],
    navigateToNextTransaction = true,
  ): Promise<void> {
    const txnIds = bankTxnIds ?? transactionsForPanel.map((t) => t.bankTxnId);

    const exitingRowIds: Record<string, boolean> = {};
    transactionsForPanel.forEach((txn) => {
      const rowId = transactionTodos.table.getRow(txn.bankTxnId, true)?.id;
      if (rowId) {
        exitingRowIds[rowId] = true;
      }
    });

    if (navigateToNextTransaction && txnIds.length > 0) {
      const nextTransaction = handleNext(txnIds[txnIds.length - 1]);

      if (nextTransaction) {
        transactionTodos.table.setRowSelection({
          [nextTransaction.bankTxnId]: true,
        });
      }
    }

    transactionTodos.table.setExitingRow(() => exitingRowIds);

    await new Promise((resolve) => setTimeout(resolve, 300));

    // We need to confirm each transaction separately because the confirmation is done per transaction.
    // Multiple confirmations is not allowed yet, but we still want to support it in the future maybe.
    await Promise.all(
      txnIds.map(async (transactionId) => {
        return approveTransactionBooking({
          bankTransactionId: transactionId,
        });
      }),
    );

    await new Promise((resolve) => setTimeout(resolve, 100));

    transactionTodos.table.setExitingRow(() => ({}));
  }

  function handleClosePanel() {
    setSelectedTransactionIds({});
    setSelectedInvoiceIds({});
  }
}

const useTransactionsBrowsingQueue = ({
  transactions,
}: {
  transactions: Transaction[];
}) => {
  const handleNext = (currentTransactionId: string) => {
    const currentIndex = transactions.findIndex(
      (t) => t.bankTxnId === currentTransactionId,
    );
    const nextIndex = currentIndex + 1;
    const nextTransaction = transactions[nextIndex];

    if (nextTransaction) {
      return nextTransaction;
    }

    return null;
  };

  const handleBack = (currentTransactionId: string) => {
    const currentIndex = transactions.findIndex(
      (t) => t.bankTxnId === currentTransactionId,
    );
    const previousIndex = currentIndex - 1;
    const previousTransaction = transactions[previousIndex];

    if (previousTransaction) {
      return previousTransaction;
    }

    return null;
  };

  return {
    handleNext,
    handleBack,
  };
};
